import type { GraphQLResult } from 'aws-amplify/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import { createBlueprintMtn, updateBlueprintMtn } from '@/services/graphql'
import { BlueprintInput } from '@/types'
import { createOptimisticBlueprintCacheEntry } from './blueprint.cache'

const client = getAmplifyClient()

type BlueprintParams = {
  upsertType: 'create' | 'update'
  input: BlueprintInput
}

export const useUpsertBlueprint = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createBlueprint'],
    mutationFn: upsertBlueprint,
    onMutate: ({ input }) => {
      queryClient.cancelQueries({
        queryKey: ['blueprint', input.id],
      })
      const previousValues = queryClient.getQueryData(['blueprint', input.id])
      queryClient.setQueryData(
        ['blueprint', input.id],
        createOptimisticBlueprintCacheEntry(input, previousValues),
      )
    },
    onSettled: (_data, _error, { input }) => {
      queryClient.invalidateQueries({
        queryKey: ['blueprint', input.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['sections', input.projectBlueprintsId],
      })
    },
  })
}

const upsertBlueprint = async (variables: BlueprintParams) => {
  return variables.upsertType === 'create'
    ? createBlueprint(variables.input)
    : updateBlueprint(variables.input)
}

const createBlueprint = async (input: BlueprintInput) => {
  const result = (await client.graphql({
    query: createBlueprintMtn,
    variables: { input },
  })) as GraphQLResult<{ createBlueprint: { createdAt: string } }>
  return result.data?.createBlueprint
}

const updateBlueprint = async (input: BlueprintInput) => {
  const result = (await client.graphql({
    query: updateBlueprintMtn,
    variables: { input },
  })) as GraphQLResult<{ updateBlueprint: { updatedAt: string } }>
  return result.data?.updateBlueprint
}
